import { defineComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import { RepositoryDetailActions, RepositoryDetailModuleState } from '~/store/repository/detail'
import { RepositoryPermissionChoices } from '~/types/types'

export const REPO_PERMS = {
  ADMIN: {
    label: 'Admin',
    value: RepositoryPermissionChoices.Admin,
    description:
      'Members will have full access to all repository settings, including the ability to add or remove members from the repository.'
  },
  WRITE: {
    label: 'Write',
    value: RepositoryPermissionChoices.Write,
    description:
      'Members will have full access to all repositories which they have access to, except the ability to add or remove members, and deactivating or activating analysis on them.'
  },
  READ: {
    label: 'Read-only',
    value: RepositoryPermissionChoices.Read,
    description:
      "Members will be able to only view the issues and metrics on private repositories they have access to, but won't be able to take any actions on issues or create Autofixes."
  }
}

export default defineComponent({
  name: 'RepoDetailMixin',

  data() {
    return {
      lastDays: 30
    }
  },

  computed: {
    ...(mapState<RepositoryDetailModuleState>('repository/detail', {
      repository: (state: RepositoryDetailModuleState) => state.repository,
      repoIdMap: (state: RepositoryDetailModuleState) => state.repoIdMap,
      repoStoreLoading: (state: RepositoryDetailModuleState) => state.loading
    }) as {
      repository: () => RepositoryDetailModuleState['repository']
      repoIdMap: () => RepositoryDetailModuleState['repoIdMap']
      repoStoreLoading: () => RepositoryDetailModuleState['loading']
    }),
    baseRouteParams(): { name: string; provider: string; owner: string } {
      const { provider, owner, repo } = this.$route.params
      return {
        name: repo as string,
        provider: provider as string,
        owner: owner as string
      }
    }
  },

  methods: {
    ...mapActions('repository/detail', {
      fetchRepoID: RepositoryDetailActions.FETCH_REPOSITORY_ID,
      fetchRepoDetails: RepositoryDetailActions.FETCH_REPOSITORY_DETAIL,
      fetchBasicRepoDetails: RepositoryDetailActions.FETCH_REPOSITORY_BASE_DETAILS,
      fetchRepoAutofixStats: RepositoryDetailActions.FETCH_REPOSITORY_AUTOFIX_STATS,
      fetchIsCommitPossible: RepositoryDetailActions.FETCH_REPOSITORY_COMMIT_POSSIBLE,
      fetchAvailableAnalyzers: RepositoryDetailActions.FETCH_AVAILABLE_ANALYZERS,
      fetchRepoRunCount: RepositoryDetailActions.FETCH_CURRENT_RUN_COUNT,
      fetchRepoPerms: RepositoryDetailActions.FETCH_REPOSITORY_PERMS,
      updateRepoWidgets: RepositoryDetailActions.UPDATE_REPO_WIDGETS,
      fetchWidgets: RepositoryDetailActions.FETCH_WIDGETS,
      fetchRepositorySettingsGeneral: RepositoryDetailActions.FETCH_REPOSITORY_SETTINGS_GENERAL,
      fetchRepositorySettingsBadges: RepositoryDetailActions.FETCH_REPOSITORY_SETTINGS_BADGES,
      fetchRepositorySettingsReporting: RepositoryDetailActions.FETCH_REPOSITORY_SETTINGS_REPORTING,
      fetchRepositorySettingsSsh: RepositoryDetailActions.FETCH_REPOSITORY_SETTINGS_SSH,
      fetchRepositorySettingsIgnoreRules:
        RepositoryDetailActions.FETCH_REPOSITORY_SETTINGS_IGNORE_RULES,
      fetchMetrics: RepositoryDetailActions.FETCH_METRICS,
      commitConfigToVcs: RepositoryDetailActions.COMMIT_CONFIG_TO_VCS,
      deleteIgnoredRule: RepositoryDetailActions.DELETE_IGNORED_RULE,
      generateSSHKey: RepositoryDetailActions.GENERATE_SSH_KEY,
      deleteSSHKey: RepositoryDetailActions.DELETE_SSH_KEY,
      toggleRepoActivation: RepositoryDetailActions.TOGGLE_REPO_ACTIVATION,
      updateRepoSettings: RepositoryDetailActions.UPDATE_REPO_SETTINGS,
      fetchIssueOccurrenceDistributionCounts:
        RepositoryDetailActions.FETCH_ISSUE_OCCURRENCE_DISTRIBUTION_COUNTS,
      fetchIssueTypeSettings: RepositoryDetailActions.FETCH_ISSUE_TYPE_SETTINGS,
      fetchAddableMembers: RepositoryDetailActions.FETCH_ADDABLE_MEMBERS,
      updateMemberPermission: RepositoryDetailActions.UPDATE_MEMBER_PERMISSION,
      removeMemberAPI: RepositoryDetailActions.REMOVE_MEMBER,
      updateRepositoryInStore: RepositoryDetailActions.UPDATE_REPOSITORY_IN_STORE,
      triggerGSRActivation: RepositoryDetailActions.TRIGGER_GSR_ACTIVATION,
      pollRepoStatus: RepositoryDetailActions.POLL_REPO_STATUS,
      triggerAdHocRun: RepositoryDetailActions.TRIGGER_ADHOC_RUN,
      regenerateRepositoryDSN: RepositoryDetailActions.REGENERATE_REPOSITORY_DSN,
      convertRepoToMonorepo: RepositoryDetailActions.CONVERT_REPO_TO_MONOREPO,
      revertMonorepo: RepositoryDetailActions.REVERT_MONOREPO
    })
  }
})
